// src/components/authentication/RedeemInviteForm.js
/**
 * RedeemInviteForm
 * ----------------
 * Onboarding form (DASH-AUTH-2) that lets an authenticated user redeem a
 * dashboard access code. On success it refreshes the auth context so the
 * dashboard access gate re-evaluates membership and admits the user.
 *
 * Used by:
 *   • AccessDenied — a signed-in but unauthorised user can onboard in place.
 *   • (re-usable) any onboarding surface that needs code redemption.
 *
 * Props
 * @param {{
 *   initialCode?: string,
 *   onSuccess?: (membership: object) => void,
 * }} props
 */

import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { redeemInvite, inviteErrorCode } from "../../api/invites";
import { inviteErrorMessage } from "../../utils/authentication/inviteMessages";

export default function RedeemInviteForm({ initialCode = "", onSuccess }) {
  const { t } = useTranslation();
  const { refreshContext } = useAuth();

  const [code, setCode] = useState(initialCode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setError(null);

      const trimmed = code.trim();
      if (!trimmed) {
        setError(inviteErrorMessage(t, "INVITE_CODE_REQUIRED"));
        return;
      }

      setSubmitting(true);
      try {
        const result = await redeemInvite(trimmed);
        // Re-read the backend membership so the gate authorises the user.
        await refreshContext();
        onSuccess?.(result?.membership);
      } catch (err) {
        setError(inviteErrorMessage(t, inviteErrorCode(err)));
      } finally {
        setSubmitting(false);
      }
    },
    [code, refreshContext, onSuccess, t],
  );

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-2">
      <label htmlFor="access-code" className="text-sm text-typography">
        {t("redeemInvite.label", "Have an access code?")}
      </label>
      <input
        id="access-code"
        type="text"
        value={code}
        onChange={(event) => setCode(event.target.value)}
        placeholder={t("redeemInvite.placeholder", "Enter access code")}
        className="custom-input w-full"
        autoComplete="off"
        disabled={submitting}
      />
      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={submitting}
        className="primary-button w-full disabled:opacity-60"
      >
        {submitting
          ? t("redeemInvite.submitting", "Redeeming…")
          : t("redeemInvite.cta", "Redeem code")}
      </button>
    </form>
  );
}
